import { createContext, useContext } from 'react'
import { type CrossClientQueryExecution, CrossClientQueryPhases } from '../../../../models/CrossClient'
import { type CcqCard, type CcqCounts, type CcqCriterionClause } from './ccqResultView'
import { type CcqWaveDisplay } from './useWaveTween'

export const CcqPhases = { INIT: 'init', PRE_WAVE: 'preWave', WAVES: 'waves', DONE: 'done' } as const

export type CcqPhase = (typeof CcqPhases)[keyof typeof CcqPhases]

export const deriveCcqPhase = (execution: CrossClientQueryExecution, inFlight: boolean, checked: number): CcqPhase => {
  if (execution.phase === CrossClientQueryPhases.INITIALIZING) {
    return CcqPhases.INIT
  }
  if (!inFlight) {
    return CcqPhases.DONE
  }
  return checked === 0 ? CcqPhases.PRE_WAVE : CcqPhases.WAVES
}

export interface CcqRun {
  execution: CrossClientQueryExecution
  clauses: CcqCriterionClause[]
  criteria: string[]
  phase: CcqPhase
  isDone: boolean
  hasCounts: boolean
  counts: CcqCounts
  // Cards whose animated count has reached 1, so a card unlocks with its badge rather than ahead of it.
  unlockedCards: Partial<Record<CcqCard, true>>
  total: number
  // How many clients a canceled search got through, when that's fewer than all of them.
  stoppedAfter: number | null
  statusCollapsed: boolean
  collapseStatus: () => void
}

export const CcqRunContext = createContext<CcqRun | null>(null)

export const useCcqRun = (): CcqRun => {
  const value = useContext(CcqRunContext)
  if (value === null) {
    throw new Error('useCcqRun must be used inside CcqRunProvider')
  }
  return value
}

// Its own context: the animated counts change every frame of a tween, and only what shows a count should redraw.
export const CcqRunDisplayContext = createContext<CcqWaveDisplay | null>(null)

export const useCcqRunDisplay = (): CcqWaveDisplay => {
  const value = useContext(CcqRunDisplayContext)
  if (value === null) {
    throw new Error('useCcqRunDisplay must be used inside CcqRunProvider')
  }
  return value
}
